import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useRef, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import Divider from '@mui/material/Divider';
import { StyledOrgDropdown, ORG_ROOT_PREFIX } from './headerOrgStyles';
import { EmbersMenu } from '../../menu/menu';
import { EmbersMenuItem } from '../../menuItem/menuItem';
var isPublicOrg = function isPublicOrg(org) {
  return org.prn === 'prn:Organization::Public';
};
var isOrgAdmin = function isOrgAdmin(permissions) {
  return permissions === null || permissions === void 0 ? void 0 : permissions.includes('rbac:BulkSuspendActivateUsers');
};
var getAllowSelectOrgs = function getAllowSelectOrgs(userPermission) {
  return userPermission.filter(function (item) {
    if (isPublicOrg(item.org)) {
      // user without admin role in Public, hide Public org
      return isOrgAdmin(item.permissions);
    } else {
      // hide sites
      return item.org.orgType !== 'Child';
    }
  });
};
export var EmbersHeaderOrg = function EmbersHeaderOrg(props) {
  var orgsListProp = props.orgsList,
    handleOrgDetailsClick = props.handleOrgDetailsClick,
    handleOrgSelect = props.handleOrgSelect,
    currentOrg = props.currentOrg,
    notificationCount = props.notificationCount,
    scrollWrapperSelector = props.scrollWrapperSelector,
    menuWithIcon = props.menuWithIcon;
  var intl = useIntl();
  var wrapper = scrollWrapperSelector ? document.querySelector(scrollWrapperSelector) : null;
  var dropdownRef = useRef(null);
  var focusRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    dropdownMenuOpen = _useState2[0],
    setDropdownMenuOpen = _useState2[1];
  var orgsList = useMemo(function () {
    return getAllowSelectOrgs(orgsListProp);
  }, [orgsListProp]);
  var userIsCurrentOrgAdmin = !!currentOrg && isOrgAdmin(currentOrg.permissions);
  // showOrgDetails: user is currentOrg admin and currentOrg is not public org
  var showOrgDetails = userIsCurrentOrgAdmin && !isPublicOrg(currentOrg.org);
  var needsMenu = orgsList.length > 1 || showOrgDetails;
  var openMenu = function openMenu() {
    focusRef.current = document.activeElement;
    if (needsMenu) {
      setDropdownMenuOpen(true);
    }
  };
  var closeOnClick = function closeOnClick() {
    setDropdownMenuOpen(false);
    if (wrapper) {
      wrapper.style.overflow = 'auto';
    }
    focusRef.current.focus();
  };
  var dropdownAriaAttributes = {
    'aria-controls': dropdownMenuOpen ? 'org-menu' : undefined,
    'aria-haspopup': needsMenu
  };
  var mappedOrgs = orgsList.map(function (item) {
    return {
      label: item.org.name,
      id: item.org.id
    };
  });
  var renderMenuItems = function renderMenuItems() {
    var menuItems = [];
    if (mappedOrgs.length > 1) {
      mappedOrgs.forEach(function (item) {
        menuItems.push( /*#__PURE__*/React.createElement(EmbersMenuItem, {
          key: item.id,
          onClick: function onClick() {
            if (handleOrgSelect) {
              handleOrgSelect(item.id);
            }
            closeOnClick();
          },
          selected: item.id === (currentOrg === null || currentOrg === void 0 ? void 0 : currentOrg.org.id)
        }, item.label));
      });
    }
    if (showOrgDetails) {
      if (menuItems.length > 0) {
        menuItems.push( /*#__PURE__*/React.createElement(Divider, {
          key: "divider",
          style: {
            margin: 8
          }
        }));
      }
      menuItems.push( /*#__PURE__*/React.createElement(EmbersMenuItem, {
        key: "org-details",
        "aria-selected": undefined,
        onClick: function onClick() {
          if (handleOrgDetailsClick) {
            handleOrgDetailsClick();
          }
          closeOnClick();
        },
        prefixIcon: /*#__PURE__*/React.createElement("span", {
          className: "icon-organization"
        })
      }, intl.formatMessage({
        id: 'embers.header.org.oneOrg.dropdownItem',
        defaultMessage: 'Organization details'
      })));
    }
    return menuItems;
  };
  var handleOpenMenuRequest = function handleOpenMenuRequest(buttonElem) {
    dropdownRef.current = buttonElem;
    openMenu();
  };
  var handleCloseMenuRequest = function handleCloseMenuRequest() {
    dropdownRef.current = null;
    closeOnClick();
  };

  // renderOrgDropdown() renders the widget which (maybe) opens the dropdown menu
  var renderOrgDropdown = function renderOrgDropdown() {
    var _classnames;
    if (orgsList.length === 0) {
      return null;
    }
    var btnClassNames = classnames((_classnames = {}, _defineProperty(_classnames, "".concat(ORG_ROOT_PREFIX, "-root"), true), _defineProperty(_classnames, "".concat(ORG_ROOT_PREFIX, "-OrgName"), true), _defineProperty(_classnames, "".concat(ORG_ROOT_PREFIX, "-hasNotification"), notificationCount), _defineProperty(_classnames, "".concat(ORG_ROOT_PREFIX, "-multiOrgWide"), !menuWithIcon && orgsList.length > 1), _defineProperty(_classnames, "".concat(ORG_ROOT_PREFIX, "-menuWithIcon"), menuWithIcon), _defineProperty(_classnames, "".concat(ORG_ROOT_PREFIX, "-noMenuNeeded"), !needsMenu), _classnames));
    var ariaLabel = intl.formatMessage({
      id: 'embers.header.eA.orgSwitch',
      defaultMessage: 'Organization: {name}'
    }, {
      name: currentOrg === null || currentOrg === void 0 ? void 0 : currentOrg.org.name
    });
    if (menuWithIcon) {
      // on tablet portrait screen there is no widget if there is no menu
      if (!needsMenu) {
        return null;
      }
      return /*#__PURE__*/React.createElement(StyledOrgDropdown, {
        className: btnClassNames,
        onOpenMenuRequest: handleOpenMenuRequest,
        onCloseMenuRequest: handleCloseMenuRequest,
        menuIsOpen: dropdownMenuOpen,
        label: ariaLabel,
        ariaProps: dropdownAriaAttributes
      }, /*#__PURE__*/React.createElement("span", {
        className: "icon-organization"
      }), dropdownMenuOpen ? /*#__PURE__*/React.createElement("span", {
        className: "icon-expand-less"
      }) : /*#__PURE__*/React.createElement("span", {
        className: "icon-expand-more"
      }));
    }
    return /*#__PURE__*/React.createElement(StyledOrgDropdown, {
      className: btnClassNames,
      onOpenMenuRequest: handleOpenMenuRequest,
      onCloseMenuRequest: handleCloseMenuRequest,
      menuIsOpen: dropdownMenuOpen,
      tabIndex: needsMenu ? 0 : -1,
      label: ariaLabel,
      ariaProps: dropdownAriaAttributes
    }, /*#__PURE__*/React.createElement("span", {
      className: "".concat(ORG_ROOT_PREFIX, "-OrgName-content")
    }, currentOrg === null || currentOrg === void 0 ? void 0 : currentOrg.org.name), needsMenu ? dropdownMenuOpen ? /*#__PURE__*/React.createElement("span", {
      className: "icon-expand-less"
    }) : /*#__PURE__*/React.createElement("span", {
      className: "icon-expand-more"
    }) : null);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, renderOrgDropdown(), /*#__PURE__*/React.createElement(EmbersMenu, {
    id: "org-menu",
    anchorEl: dropdownRef.current,
    open: dropdownMenuOpen,
    onClose: closeOnClick,
    placement: "bottom-end",
    verticalOffset: 6
  }, renderMenuItems()));
};